import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Upload } from 'lucide-react';
import { RestorePanelProps } from './types';
import { formatDate } from './utils';

const RestorePanel: React.FC<RestorePanelProps> = ({ backups, loading, onRestoreBackup }) => {
  const [selectedId, setSelectedId] = React.useState('');
  const [overwrite, setOverwrite] = React.useState(true);
  const [dryRun, setDryRun] = React.useState(false);

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
          Restore Backup
        </h3>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3" />
            <div>
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Restore Warning
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Restoring a backup will overwrite existing DNS records. Consider creating a fresh
                backup first.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Select Backup
            </label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose a backup...</option>
              {backups.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.zoneName} - {formatDate(b.timestamp)} ({b.recordCount} records)
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center justify-between p-3 border rounded-lg dark:border-slate-700">
              <span>
                <span className="block text-sm font-medium">Overwrite Existing Records</span>
                <span className="block text-xs text-slate-500">
                  Replace existing records with backup data
                </span>
              </span>
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
              />
            </label>

            <label className="flex items-center justify-between p-3 border rounded-lg dark:border-slate-700">
              <span>
                <span className="block text-sm font-medium">Dry Run</span>
                <span className="block text-xs text-slate-500">
                  Preview changes without applying
                </span>
              </span>
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
            </label>
          </div>

          <div className="flex justify-end space-x-3">
            <button className="px-4 py-2 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
              Cancel
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!selectedId || loading}
              onClick={() => onRestoreBackup(selectedId, { overwrite, dryRun })}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              <Upload className="w-4 h-4 mr-2 inline" />
              Restore Backup
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestorePanel;
